import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Save, RefreshCw, Target } from "lucide-react";
import { toast } from "sonner";

const MONTH_LABEL = (key) => {
  const [y, m] = key.split("-");
  const d = new Date(Number(y), Number(m) - 1, 1);
  return d.toLocaleString("en-GB", { month: "short", year: "2-digit" });
};

// Fields admins can edit. For 'sales' only the target — actuals come from CRM.
// For Xero-sourced rows, BOTH target and actual are editable until Xero is wired.
const EDITABLE = [
  { key: "sales", label: "Sales (Closed Won)", source: "portal", fields: ["target"] },
  { key: "mrr", label: "MRR", source: "xero", fields: ["target", "actual"] },
  { key: "one_off", label: "One-Off Revenue", source: "xero", fields: ["target", "actual"] },
  { key: "mdf", label: "MDF Revenue", source: "xero", fields: ["target", "actual"] },
  { key: "total_revenue", label: "Total Revenue", source: "xero", fields: ["target", "actual"] },
  { key: "gm", label: "GM (£)", source: "xero", fields: ["target", "actual"] },
  { key: "gm_pct", label: "GM %", source: "xero", fields: ["target", "actual"] },
  { key: "opex", label: "Opex Costs", source: "xero", fields: ["target", "actual"] },
  { key: "net_profit", label: "Net Profit", source: "xero", fields: ["target", "actual"] },
];

export default function BudgetTargetsPanel() {
  const [fys, setFys] = useState([]);
  const [fyStart, setFyStart] = useState(null);
  const [data, setData] = useState(null);
  const [edits, setEdits] = useState({}); // {month: {field: value}}
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  const [refreshTick, setRefreshTick] = useState(0);
  const reload = () => setRefreshTick((t) => t + 1);

  useEffect(() => {
    (async () => {
      const r = await api.get("/budget-tracker/fys");
      setFys(r.data);
      const current = r.data[1] || r.data[0];
      setFyStart(current.fy_start_year);
    })();
  }, []);

  useEffect(() => {
    if (fyStart == null) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(`/budget-tracker?fy_start_year=${fyStart}`);
        if (cancelled) return;
        setData(r.data);
        setEdits({});
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [fyStart, refreshTick]);

  const setCell = (month, field, value) => {
    setEdits((prev) => {
      const next = { ...prev };
      next[month] = { ...(next[month] || {}), [field]: value };
      return next;
    });
  };

  const cellValue = (month, field) => {
    if (edits[month]?.[field] !== undefined) return edits[month][field];
    const mo = data?.months.find((m) => m.month === month);
    const raw = mo?.[field];
    return raw == null ? "" : String(raw);
  };

  const dirtyCount = useMemo(() => Object.values(edits).reduce((s, m) => s + Object.keys(m).length, 0), [edits]);

  const save = async () => {
    if (!dirtyCount) return;
    setSaving(true);
    try {
      const months = Object.keys(edits);
      for (const m of months) {
        const payload = {};
        for (const [k, v] of Object.entries(edits[m])) {
          payload[k] = v === "" ? 0 : Number(v);
        }
        // eslint-disable-next-line no-await-in-loop
        await api.patch(`/budget-tracker/${fyStart}/${m}`, payload);
      }
      toast.success(`Saved ${dirtyCount} change${dirtyCount === 1 ? "" : "s"} for ${data.label}`);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (!data || loading) {
    return <div className="text-sm font-mono text-slate-500" data-testid="budget-targets-loading">Loading budget targets…</div>;
  }

  return (
    <div className="space-y-5" data-testid="budget-targets-panel">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="space-y-1">
          <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-blue-700 flex items-center gap-1.5">
            <Target className="h-3 w-3" /> Board Pack Targets
          </div>
          <h3 className="font-heading text-xl font-bold text-slate-900">Budget Targets &amp; Xero Actuals — {data.label}</h3>
          <p className="text-xs text-slate-500 max-w-2xl">
            Set monthly targets per FY. Sales actuals are derived from Closed Won deals automatically. Xero-sourced actuals are manual entry until the Xero integration is wired.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={String(fyStart)} onValueChange={(v) => setFyStart(Number(v))}>
            <SelectTrigger data-testid="budget-targets-fy-select" className="w-[170px] bg-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {fys.map((f) => (
                <SelectItem key={f.fy_start_year} value={String(f.fy_start_year)}>
                  {f.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            data-testid="budget-targets-reload"
            variant="outline" size="sm"
            onClick={reload}
            className="gap-1.5"
          >
            <RefreshCw className="h-3.5 w-3.5" /> Reload
          </Button>
          <Button
            data-testid="budget-targets-save"
            size="sm"
            disabled={!dirtyCount || saving}
            onClick={save}
            className="gap-1.5 bg-blue-600 hover:bg-blue-700"
          >
            <Save className="h-3.5 w-3.5" /> Save {dirtyCount ? `(${dirtyCount})` : ""}
          </Button>
        </div>
      </div>

      <Card className="border-slate-200 overflow-hidden">
        <CardHeader className="border-b border-slate-100 py-3">
          <CardTitle className="text-sm font-heading text-slate-700">
            Monthly grid — Apr to Mar
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-xs" data-testid="budget-targets-table">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="sticky left-0 bg-slate-50 z-10 px-3 py-3 text-left font-mono text-[10px] uppercase tracking-wider text-slate-500 min-w-[220px]">
                  Metric
                </th>
                <th className="px-2 py-3 text-center font-mono text-[10px] uppercase tracking-wider text-slate-500">T/A</th>
                {data.months.map((m) => (
                  <th key={m.month} className="px-2 py-3 text-center font-mono text-[10px] uppercase tracking-wider text-slate-500 min-w-[100px]">
                    {MONTH_LABEL(m.month)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {EDITABLE.map((metric, idx) => (
                <React.Fragment key={metric.key}>
                  {metric.fields.map((f, fi) => (
                    <tr key={`${metric.key}-${f}`} className={`border-t border-slate-100 ${idx % 2 === 0 ? "bg-white" : "bg-slate-50/30"}`}>
                      <td className="sticky left-0 bg-inherit px-3 py-1.5">
                        {fi === 0 ? (
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-slate-900">{metric.label}</span>
                            <Badge variant="outline" className={`text-[9px] px-1 py-0 ${metric.source === "portal" ? "bg-blue-50 text-blue-700 border-blue-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>
                              {metric.source === "portal" ? "PORTAL" : "XERO"}
                            </Badge>
                          </div>
                        ) : (
                          <span className="text-[11px] text-slate-400">&nbsp;</span>
                        )}
                      </td>
                      <td className="px-2 py-1.5 text-center font-mono text-[10px] text-slate-400 uppercase">
                        {f === "target" ? "TGT" : "ACT"}
                      </td>
                      {data.months.map((mo) => {
                        const field = `${metric.key}_${f}`;
                        // Sales actual: read-only (auto from CRM)
                        const readOnly = metric.key === "sales" && f === "actual";
                        if (readOnly) {
                          return (
                            <td key={mo.month + field} className="px-2 py-1.5 text-right font-mono text-slate-500">
                              {Number(mo[field] || 0).toLocaleString("en-GB", { maximumFractionDigits: 0 })}
                            </td>
                          );
                        }
                        return (
                          <td key={mo.month + field} className="px-1 py-1">
                            <Input
                              data-testid={`budget-input-${metric.key}-${mo.month}-${f}`}
                              type="number"
                              step="0.01"
                              className={`h-8 text-right font-mono text-xs px-1.5 ${edits[mo.month]?.[field] !== undefined ? "border-blue-400 bg-blue-50" : ""}`}
                              value={cellValue(mo.month, field)}
                              onChange={(e) => setCell(mo.month, field, e.target.value)}
                            />
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <div className="text-[11px] text-slate-400 font-mono">
        {data.updated_at && (
          <>Last saved {new Date(data.updated_at).toLocaleString("en-GB")} {data.updated_by ? `by ${data.updated_by}` : ""}.</>
        )}
      </div>
    </div>
  );
}
